import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import usePageTitle from '../hooks/usePageTitle';
import { logger } from '../utils/logger';
import PublicPageLayout from '../components/layout/PublicPageLayout';
import CanonicalLink from '../components/common/CanonicalLink';

const SupportPage: React.FC = () => {
  // Set the page title
  usePageTitle('Contact Us | WaveScoop', false);
  
  // Form state
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    // Check if all fields are filled
    if (!Object.values(formState).every(value => value.trim() !== '')) {
      setIsSubmitting(false);
      setSubmitSuccess(false);
      setSubmitError('Please fill in all fields');
      return;
    }

    try {
      // Use the API URL from environment variables
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/contact`;
      logger.info('Sending form data to:', apiUrl);
      
      // Send the form data to the backend API
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formState,
          to: 'support@wavescoop.com',
        }),
      });

      // Log the response for debugging
      logger.info('Response status:', response.status);
      
      let responseData;
      
      // Check if response is ok first, before trying to read the body
      if (!response.ok) {
        // For error responses, get text since it might not be JSON
        const errorText = await response.text();
        try {
          // Try to parse as JSON in case it is a structured error
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.error || `Server error: ${response.status}`);
        } catch (jsonError) {
          // If not JSON, use the text directly
          throw new Error(errorText || `Server error: ${response.status}`);
        }
      }
      
      // For success responses, try to parse as JSON
      try {
        responseData = await response.json();
        logger.info('Response data:', responseData);
      } catch (jsonError) {
        logger.error('Error parsing JSON response:', jsonError);
        throw new Error('Invalid server response');
      }

      setSubmitSuccess(true);
      setSubmitError('');
      // Clear form
      setFormState({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      logger.error('Error sending message:', error);
      setSubmitSuccess(false);
      setSubmitError(`Failed to send message: ${(error as Error).message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicPageLayout>
      <Helmet>
        <meta 
          name="description" 
          content="Contact our support team for help with WaveScoop. We're here to help you get the most out of our platform." 
        />
      </Helmet>
      <CanonicalLink />
      <Box sx={{ flexGrow: 1, py: { xs: 2, md: 4 } }}>
        <Container maxWidth="lg">
          <Box sx={{ my: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ color: 'white' }}>
              Contact Us
            </Typography>
            <Typography variant="h5" component="h2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              We're here to help you get the most out of our platform.
            </Typography>
          </Box>

          <Box id="contact" sx={{ mt: 5, mb: 4 }}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 2, sm: 3, md: 4 }, 
                bgcolor: 'rgba(10, 42, 77, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon fontSize="large" sx={{ mr: 2, color: 'white' }} />
                <Typography variant="h4" component="h2" sx={{ color: 'white' }}>
                  Contact Us
                </Typography>
              </Box>
              <Divider sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.2)' }} />
              
              {/* <Typography paragraph sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Can't find the answer you're looking for? Please fill out the form below and our support team will get back to you within 24 hours.
              </Typography> */}
              
              {submitSuccess && (
                <Alert severity="success" sx={{ mb: 3, bgcolor: 'success.dark', color: 'white' }}>
                  Thank you for your message! We'll get back to you as soon as possible.
                </Alert>
              )}
              
              {submitError && (
                <Alert severity="error" sx={{ mb: 3, bgcolor: 'error.dark', color: 'white' }}>
                  {submitError}
                </Alert>
              )}
              
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      id="name"
                      label="Your Name"
                      name="name"
                      value={formState.name}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                      InputProps={{ sx: { color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formState.email}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                      InputProps={{ sx: { color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id="subject"
                      label="Subject"
                      name="subject"
                      value={formState.subject}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                      InputProps={{ sx: { color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      id="message"
                      label="Your Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formState.message}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                      InputProps={{ sx: { color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                      sx={{ color: 'white', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>
    </PublicPageLayout>
  );
};

export default SupportPage; 